import { useState } from "react";
import { useT } from "../i18n";
import ChipRow from "./ChipRow";

/** The part sizes offered, in characters (ADR-0029). The server accepts 200–3000. */
const PART_SIZES = [200, 250, 350, 500, 800, 1200, 2000, 3000];
export const DEFAULT_PART_CHARS = 350;

/**
 * Roughly how long a part of this size plays, at 3.1 Flash's measured pace
 * (11.6 characters a second plus a 5.3-second lead-in). Shown so a size is
 * chosen by how long the model has to hold a voice, which is what the choice
 * is about — not by a character count nobody can hear.
 */
const secondsFor = (chars: number) => Math.round(5.3 + chars / 11.6);

/**
 * PartSettings chooses how a book's chapters are cut into requests: how long
 * each part is, and whether each part is recorded starting from the sentence
 * before it, which is then cut out (ADR-0029, ADR-0030).
 *
 * There is no "give it as a hint" option. A hinted sentence was sometimes read
 * aloud, and a line the narrator only sometimes speaks ruins a book — so the
 * sentence is either not given at all, or always read and always cut.
 *
 * A model with a part-size cap (Soniox, which stops speaking at 3 minutes —
 * ADR-0031) is offered nothing larger, and a larger saved size shows as the cap,
 * which is what the server splits at.
 *
 * Like the voice and the model, changing either saves on the book and
 * re-narrates nothing.
 */
export default function PartSettings({
  chunkChars,
  readContext,
  maxChars,
  onChunkChars,
  onReadContext,
  offersReadContext = true,
  disabled = false,
}: {
  /** The book's part size, or 0 for the default. */
  chunkChars: number;
  readContext: boolean;
  /** The largest part size the book's model takes, when it has a limit. */
  maxChars?: number;
  onChunkChars: (chars: number) => void;
  onReadContext: (read: boolean) => void;
  /** False on a model that takes no lead-in, where the server refuses it (ADR-0113). */
  offersReadContext?: boolean;
  disabled?: boolean;
}) {
  const t = useT();
  const cap = maxChars ?? Infinity;
  // Shown as chosen the moment they are picked, not when the save comes back:
  // a control bound only to the server stays put through the round trip, which
  // on a phone reads as a tap that did not land. The page keys this component
  // on the book's saved values, so it starts again from the server's answer.
  const [value, setValue] = useState(Math.min(chunkChars || DEFAULT_PART_CHARS, cap));
  const [read, setRead] = useState(readContext);
  const offered = PART_SIZES.filter((chars) => chars <= cap);
  // A size set some other way still shows as itself rather than as a blank.
  const sizes = offered.includes(value) ? offered : [...offered, value].sort((a, b) => a - b);

  return (
    <div className="mt-3 space-y-2" data-testid="part-settings">
      {/* Chips, not a <select>: every other choice on this screen is a row of
          pills, and the owner rejects the native wheel in the studio. */}
      <div className="text-sm">
        <span className="mb-1 block text-xs text-muted">{t.voice.partSizeLabel}</span>
        <ChipRow
          legend={t.voice.partSizeLabel}
          options={sizes.map((chars) => ({
            value: String(chars),
            label: t.voice.partSizeChip(chars),
            testID: `part-size-${chars}`,
          }))}
          value={String(value)}
          disabled={disabled}
          onChange={(v) => {
            const chars = Number(v);
            setValue(chars);
            onChunkChars(chars);
          }}
        />
        <span className="mt-1 block text-xs text-muted" data-testid="part-size-note">
          {t.voice.partSizeOption(value, secondsFor(value))}
          {value === DEFAULT_PART_CHARS ? t.voice.partSizeDefault : ""}
        </span>
        {maxChars !== undefined && (
          <span className="mt-1 block text-xs text-muted" data-testid="part-size-cap">
            {t.voice.partSizeCapped(maxChars)}
          </span>
        )}
      </div>
      {offersReadContext && (
        <label className="flex cursor-pointer items-start gap-2 text-sm text-ink-soft select-none">
          <input
            type="checkbox"
            data-testid="read-context"
            checked={read}
            disabled={disabled}
            onChange={(e) => {
              setRead(e.target.checked);
              onReadContext(e.target.checked);
            }}
            className="mt-1 size-3.5 accent-[var(--c-sage)]"
          />
          <span>
            {t.voice.readLabel}
            <span className="block text-xs text-muted" data-testid="read-note">
              {t.voice.readNote}
            </span>
          </span>
        </label>
      )}
    </div>
  );
}
